/**
 * The Customize dialog (1.AC-2 deliverable 18): an editable homebrew copy of an SRD creature.
 * `CustomizeDialogProvider` renders one kit `Dialog`, opened by `useCustomizeDialog().open(request)`;
 * the dialog runs no editor command and does not navigate, the caller does. A spell or item source
 * takes the card branch: 2.M's `CardCustomizeProvider`, mounted inside this provider, asks instead
 * and its answer resolves this `open` with `sheetId: null` (2.M deliverable 22).
 */
import { typesList } from '@codex/contracts';
import {
  Button,
  Checkbox,
  Dialog,
  Skeleton,
  TextField,
  TYPE_KEYS,
  TypeChip,
  type TypeKey,
} from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from '../../auth/client.js';
import {
  CardCustomizeProvider,
  useCardCustomize,
} from '../../sheets/cards/variant/CardCustomize.js';
import { safeFlags } from '../../shell/frame-rule.js';
import { useNotify } from '../../shell/notices.js';
import { useShellSession } from '../../shell/session.js';
import { COPY } from '../copy.js';
import { typesKey } from '../data/hooks.js';
import {
  useCreateVariant,
  useHomeHits,
  useReferenceEntry,
  useSheetOwner,
} from '../data/variant.js';
import {
  CUSTOMIZE_GENERIC,
  type CustomizeErrors,
  type CustomizeResult,
  customizeErrors,
  customizeResult,
  EMPTY_NAME,
  sheetTypes,
} from '../model/customize.js';

export type CustomizeRequest =
  | { readonly sourceDocId: string; readonly title: string; readonly offerRepoint: boolean }
  | { readonly sourceSheetId: string; readonly offerRepoint: boolean };

export type { CustomizeResult } from '../model/customize.js';

export interface CustomizeDialogApi {
  readonly open: (request: CustomizeRequest) => Promise<CustomizeResult | null>;
}

const NONE: CustomizeDialogApi = { open: async () => null };
const DialogContext = createContext<CustomizeDialogApi>(NONE);

export function useCustomizeDialog(): CustomizeDialogApi {
  return useContext(DialogContext);
}

interface Active {
  readonly request: CustomizeRequest;
  readonly resolve: (result: CustomizeResult | null) => void;
}

export function CustomizeDialogProvider({ children }: { readonly children?: ReactNode }) {
  const me = useShellSession();
  const on = safeFlags(me)?.statblock_form === true;
  const [active, setActive] = useState<Active | null>(null);
  const activeRef = useRef<Active | null>(null);
  activeRef.current = active;

  const open = useCallback(
    (request: CustomizeRequest): Promise<CustomizeResult | null> => {
      if (!on || activeRef.current !== null) return Promise.resolve(null);
      return new Promise((resolve) => setActive({ request, resolve }));
    },
    [on],
  );
  const finish = useCallback((result: CustomizeResult | null) => {
    activeRef.current?.resolve(result);
    setActive(null);
  }, []);
  const value = useMemo(() => ({ open }), [open]);
  return (
    <DialogContext.Provider value={value}>
      <CardCustomizeProvider>
        {children}
        {active !== null && <CustomizeDialog request={active.request} onDone={finish} />}
      </CardCustomizeProvider>
    </DialogContext.Provider>
  );
}

/** A user type the design has no glyph for shows the note glyph. */
function glyphFor(typeKey: string): TypeKey {
  return (TYPE_KEYS as readonly string[]).includes(typeKey) ? (typeKey as TypeKey) : 'note';
}

interface HomePick {
  readonly id: string;
  readonly title: string;
  readonly typeKey: string;
}

function CustomizeDialog({
  request,
  onDone,
}: {
  readonly request: CustomizeRequest;
  readonly onDone: (result: CustomizeResult | null) => void;
}) {
  const notify = useNotify();
  const fromSheet = 'sourceSheetId' in request;
  const owner = useSheetOwner(fromSheet ? request.sourceSheetId : null);
  const sourceDocId = fromSheet ? owner.documentId : request.sourceDocId;
  const source = useReferenceEntry(sourceDocId);
  const types = useQuery({
    queryKey: typesKey,
    queryFn: async () => (await api(typesList)).items,
    retry: false,
  });
  const create = useCreateVariant(sourceDocId);

  const sourceTitle = source.entry?.document.title ?? ('title' in request ? request.title : '');
  const options = useMemo(() => sheetTypes(types.data ?? []), [types.data]);
  const notCreature = source.entry !== undefined && source.entry.kind !== 'creature';
  const loadFailed = owner.failed || source.failure !== null || types.isError;
  const ready = source.entry !== undefined && types.data !== undefined && !notCreature;

  // The card branch: a spell or item source (the cached `reference.get` kind) asks 2.M's dialog.
  const card = useCardCustomize();
  const handedRef = useRef(false);
  useEffect(() => {
    if (!notCreature || handedRef.current || !sourceDocId) return;
    handedRef.current = true;
    void card
      .open({ sourceDocId, title: sourceTitle, offerRepoint: request.offerRepoint })
      .then((r) => onDone(r && { ...r, sheetId: null }));
  }, [notCreature, card, sourceDocId, sourceTitle, request.offerRepoint, onDone]);

  const nameId = useId();
  const nameField = () =>
    document.querySelector<HTMLInputElement>('.cx-sf__customize input[name="title"]');
  const [name, setName] = useState<string | null>(null);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [home, setHome] = useState<HomePick | null>(null);
  const [repoint, setRepoint] = useState(true);
  const [errors, setErrors] = useState<CustomizeErrors>({});

  const nameValue = name ?? sourceTitle;
  const typeValue = typeId ?? options[0]?.id ?? '';

  const focused = useRef(false);
  useEffect(() => {
    if (!ready || focused.current) return;
    focused.current = true;
    const input = document.querySelector<HTMLInputElement>('.cx-sf__customize input[name="title"]');
    input?.focus();
    input?.select();
  }, [ready]);

  const submit = async () => {
    if (nameValue.trim() === '') {
      setErrors({ name: EMPTY_NAME });
      nameField()?.focus();
      return;
    }
    setErrors({});
    const fields = {
      name: nameValue,
      typeId: typeValue,
      homeId: home?.id ?? null,
      repoint,
    };
    try {
      const answer = await create.mutateAsync(fields);
      const result = customizeResult(answer, fields, request.offerRepoint);
      notify(`Created ${result.title}.`);
      onDone(result);
    } catch (error) {
      setErrors(customizeErrors(error));
    }
  };

  if (notCreature) return null;
  const busy = create.isPending;
  const title = sourceTitle === '' ? 'Customize' : `Customize ${sourceTitle}`;
  return (
    <Dialog
      open
      title={title}
      description="Make an editable homebrew copy. The SRD entry stays as it is."
      busy={busy}
      onClose={() => onDone(null)}
      actions={
        <>
          <Button variant="ghost" onClick={() => onDone(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!ready} onClick={() => void submit()}>
            Create copy
          </Button>
        </>
      }
    >
      {loadFailed ? (
        <div>
          <p role="alert">{COPY.compareFailed}</p>
          <Button
            variant="secondary"
            onClick={() => {
              owner.retry();
              source.retry();
              void types.refetch();
            }}
          >
            Try again
          </Button>
        </div>
      ) : !ready ? (
        <div>
          <p role="status" className="cx-visually-hidden">
            {COPY.compareLoading}
          </p>
          <div aria-hidden="true">
            <Skeleton lines={4} variant="card" label={COPY.compareLoading} />
          </div>
        </div>
      ) : (
        <form
          className="cx-sf__customize"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <TextField
            name="title"
            label="Name"
            required
            value={nameValue}
            maxLength={200}
            aria-invalid={errors.name !== undefined || undefined}
            description={errors.name}
            onChange={(event) => setName(event.currentTarget.value)}
          />
          <div className="cx-field">
            <label htmlFor={`${nameId}-type`} className="cx-field__label">
              Type
            </label>
            <select
              id={`${nameId}-type`}
              className="cx-sf__select"
              value={typeValue}
              aria-invalid={errors.type !== undefined || undefined}
              aria-describedby={errors.type !== undefined ? `${nameId}-type-error` : undefined}
              onChange={(event) => setTypeId(event.currentTarget.value)}
            >
              {options.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
            {errors.type !== undefined && (
              <span id={`${nameId}-type-error`} className="cx-sf__issue">
                {errors.type}
              </span>
            )}
          </div>
          <HomeField home={home} onPick={setHome} error={errors.home} />
          {request.offerRepoint && (
            <Checkbox
              label="Point this embed at the copy"
              checked={repoint}
              onChange={setRepoint}
            />
          )}
          {errors.message !== undefined && (
            <p role="alert" className="cx-sf__issue">
              {errors.message || CUSTOMIZE_GENERIC}
            </p>
          )}
        </form>
      )}
    </Dialog>
  );
}

function HomeField({
  home,
  onPick,
  error,
}: {
  readonly home: HomePick | null;
  readonly onPick: (home: HomePick | null) => void;
  readonly error: string | undefined;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 150);
    return () => clearTimeout(timer);
  }, [text]);
  const hits = useHomeHits(query, home === null && query !== '');
  const list = text.trim() === query && query !== '' ? (hits.data ?? []) : [];
  const current = Math.min(active, Math.max(list.length - 1, 0));
  const choose = (index: number) => {
    const hit = list[index];
    if (!hit) return;
    onPick({ id: hit.id, title: hit.title, typeKey: hit.typeKey });
    setText('');
    setQuery('');
  };
  if (home !== null) {
    return (
      <div className="cx-field">
        <span className="cx-field__label">Home</span>
        <div className="cx-sf__home">
          <TypeChip type={glyphFor(home.typeKey)} title={home.title} />
          <Button
            size="sm"
            variant="ghost"
            aria-invalid={error !== undefined || undefined}
            aria-describedby={error !== undefined ? `${id}-error` : undefined}
            onClick={() => onPick(null)}
          >
            Clear home
          </Button>
        </div>
        {error !== undefined && (
          <span id={`${id}-error`} className="cx-sf__issue">
            {error}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="cx-field">
      <label htmlFor={id} className="cx-field__label">
        Home
      </label>
      <input
        id={id}
        type="text"
        role="combobox"
        className="cx-sf__select"
        aria-expanded={list.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={list.length > 0 ? `${listId}-${current}` : undefined}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={error !== undefined ? `${id}-error` : undefined}
        placeholder="Optional: a page to put the copy in"
        value={text}
        onChange={(event) => {
          setText(event.currentTarget.value);
          setActive(0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            if (list.length > 0) {
              event.preventDefault();
              choose(current);
            }
          } else if (event.key === 'ArrowDown' && list.length > 0) {
            event.preventDefault();
            setActive((current + 1) % list.length);
          } else if (event.key === 'ArrowUp' && list.length > 0) {
            event.preventDefault();
            setActive((current - 1 + list.length) % list.length);
          }
        }}
      />
      <div id={listId} role="listbox" aria-label="Home results" hidden={list.length === 0}>
        {list.map((hit, index) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keyboard
          <div
            key={hit.id}
            id={`${listId}-${index}`}
            role="option"
            tabIndex={-1}
            aria-selected={index === current}
            className="cx-sf__option"
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => choose(index)}
          >
            {hit.title}
          </div>
        ))}
      </div>
      {error !== undefined && (
        <span id={`${id}-error`} className="cx-sf__issue">
          {error}
        </span>
      )}
    </div>
  );
}
